<template>
  <div>
    <loader v-if="isLoading && !hasRunningRun" />

    <template v-if="isCurrentRoute">
      <h1 class="mb-3">{{ $t('views.smmarketingcomparison.title') }}</h1>

      <b-row class="mb-3" align-h="between">
        <b-col cols="auto"></b-col>
        <b-col cols="auto">
          <b-button variant="primary" :disabled="isBusy" @click="startComparison">
            <b-spinner v-if="isBusy" small class="mr-2" />
            {{ $t('views.smmarketingcomparison.button.trigger') }}
          </b-button>
        </b-col>
      </b-row>

      <b-table
        :items="runs"
        :fields="runFields"
        striped
        hover
        responsive
        show-empty
        :empty-text="$t('views.smmarketingcomparison.table.empty')"
        @row-clicked="onRunClicked"
        tbody-tr-class="has-routerlink"
      >
        <template #cell(runDate)="data">
          {{ formatDate(data.item.runDate) }}
        </template>
        <template #cell(runStatus)="data">
          <b-badge :variant="statusVariant(data.item.runStatus)">{{ statusLabel(data.item.runStatus) }}</b-badge>
        </template>
      </b-table>
    </template>

    <router-view v-else />
  </div>
</template>

<script>
import Loader from '@/components/Loader'

const POLL_INTERVAL_MS = 3000

export default {
  name: 'SmMarketingComparison',
  components: {
    Loader
  },
  data () {
    return {
      isTriggering: false,
      pollTimer: null,
      runFields: [
        { key: 'runDate', label: this.$t('views.smmarketingcomparison.table.label.runDate') },
        { key: 'runStatus', label: this.$t('views.smmarketingcomparison.table.label.status') },
        { key: 'totalCompared', label: this.$t('views.smmarketingcomparison.table.label.totalCompared') },
        { key: 'mismatchCount', label: this.$t('views.smmarketingcomparison.table.label.mismatchCount') }
      ]
    }
  },
  computed: {
    isCurrentRoute () {
      return this.$route.name === 'SmMarketingComparison'
    },
    isLoading () {
      return this.$store.state.smmarketingcomparison.isLoading
    },
    runs () {
      return this.$store.state.smmarketingcomparison.runs
    },
    hasRunningRun () {
      return this.runs.some(run => this.isRunningStatus(run.runStatus))
    },
    isBusy () {
      return this.isTriggering || this.hasRunningRun
    }
  },
  methods: {
    formatDate (value) {
      if (!value) return ''
      return new Date(value).toLocaleString('de-CH')
    },
    resolveStatusKey (status) {
      const statusMap = { 0: 'Running', 1: 'Completed', 2: 'Failed' }
      return typeof status === 'number' ? statusMap[status] : status
    },
    isRunningStatus (status) {
      return this.resolveStatusKey(status) === 'Running'
    },
    statusLabel (status) {
      const statusKey = this.resolveStatusKey(status)
      const key = `views.smmarketingcomparison.status.${statusKey}`
      const translated = this.$t(key)
      return translated !== key ? translated : statusKey
    },
    statusVariant (status) {
      const statusKey = this.resolveStatusKey(status)
      if (statusKey === 'Completed') return 'success'
      if (statusKey === 'Failed') return 'danger'
      return 'warning'
    },
    onRunClicked (item) {
      this.$router.push({
        name: 'SmMarketingComparison.Detail',
        params: { runid: item.id }
      })
    },
    startComparison () {
      if (this.isBusy) {
        return
      }

      this.isTriggering = true
      this.$store.dispatch('smmarketingcomparison/triggerComparison')
        .then(() => this.refreshRuns())
        .then(() => this.startPolling())
        .catch(error => {
          if (error?.response?.status === 409) {
            this.$bvToast.toast(this.$t('views.smmarketingcomparison.errors.alreadyRunning'), {
              title: this.$t('views.smmarketingcomparison.title'),
              variant: 'warning',
              solid: true
            })
            return this.refreshRuns().then(() => this.startPolling())
          }
          return Promise.reject(error)
        })
        .finally(() => {
          this.isTriggering = false
        })
    },
    refreshRuns () {
      return this.$store.dispatch('smmarketingcomparison/loadRuns', { silent: true })
    },
    loadRuns () {
      return this.$store.dispatch('smmarketingcomparison/loadRuns')
    },
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        this.refreshRuns()
          .then(() => {
            if (!this.hasRunningRun) {
              this.stopPolling()
            }
          })
      }, POLL_INTERVAL_MS)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  },
  created () {
    this.loadRuns()
      .then(() => {
        if (this.hasRunningRun) {
          this.startPolling()
        }
      })
  },
  beforeDestroy () {
    this.stopPolling()
  }
}
</script>
